import type { DetailSection, DosageRow, Product, SpecRow } from '../types';

/**
 * THE FACT EXTRACTOR. Turns the backend's free-form `product_details.sections` into named, typed
 * facts — composition rows, specifications, the dose table, application methods, usage steps,
 * precautions, packaging, shelf life and the approval/licence wording.
 *
 * WHY THIS EXISTS. The catalogue's richest content — CFU counts, per-crop doses, pH, shelf life,
 * the NPOP/FCO wording — is stored as an ARRAY OF UNTYPED SECTIONS, because staff author it in the
 * dashboard and the shape has to stay open. The product page rendered those sections generically, as
 * prose inside accordions, which is fine for a reader and useless to a machine: nothing could answer
 * "what is AGN made of" or "how much AGN per acre for grapes" without parsing a paragraph.
 *
 * Reading the sections ONCE, here, and naming what they contain is what lets the same facts feed the
 * product page's tables, the Product JSON-LD's `additionalProperty`, each crop page's dose table,
 * llms.txt and the per-product Markdown mirror — from ONE source of truth. The backend stays the
 * single source of truth; this only recognises what it already says.
 *
 * NOTHING IS DERIVED, CONVERTED OR FILLED IN. A dose is copied verbatim from its cell. A missing
 * value stays missing and the UI omits the row rather than guessing — a wrong dose is a damaged crop.
 *
 * Two authoring shapes exist in production and both are handled:
 *   1. Seeded products carry stable ids (`composition`, `dosage`, `packaging`, …).
 *   2. Hand-authored products (C4L Biofoliar) carry random ids and free-text titles
 *      ("Specification", "Product Licence", "More Information", "Warrenty").
 * So ids are tried first and titles second. Neither is trusted to exist.
 */

export interface ProductFacts {
  /** Intro paragraphs (the promoted Overview / Product Description section). */
  description: string[];
  compositionSummary?: string;
  compositionRows: SpecRow[];
  specifications: SpecRow[];
  dosageSummary?: string;
  dosageTable?: { columns: string[]; rows: DosageRow[]; caption?: string };
  /** Dose facts authored as label/value pairs rather than a table (e.g. C4L Biofoliar). */
  dosageNotes: SpecRow[];
  applicationMethods: string[];
  usage: string[];
  precautions: string[];
  packagingInfo?: string;
  shelfLife?: string;
  /** Approval / licence wording, copied verbatim. Never normalised, never summarised. */
  licence?: string;
  /** Warranty / liability wording where the product sheet carries one. */
  warranty?: string;
  /** Sections that carried real content but matched no known slot — still rendered, never dropped. */
  other: DetailSection[];
}

const RE = {
  overview: /^(overview|product description|about|description|what is)/i,
  composition: /composition|ingredient|formula/i,
  spec: /specification|technical data|parameter|propert/i,
  dosage: /dosage|dose|application rate|rate of application|programme/i,
  usage: /how to use|usage|direction|method of application|instructions/i,
  precaution: /precaution|caution|safety|warning|hazard/i,
  packaging: /packag|storage|shelf|more information/i,
  licence: /licen[cs]|approval|certifi|registration|registered/i,
  warranty: /warrant|warrent|liabilit|disclaimer/i,
  benefits: /^benefit|^key benefit/i,
};

/** True when a section carries no content at all — an empty authoring artifact, not a fact. */
export function isEmptySection(s: DetailSection): boolean {
  const hasRows = (s.rows ?? []).some((r) => (r ?? []).some((c) => String(c ?? '').trim()));
  return (
    !(s.text ?? '').trim() &&
    !(s.items ?? []).some((i) => String(i ?? '').trim()) &&
    !(s.pairs ?? []).some((p) => String(p?.value ?? '').trim() || String(p?.label ?? '').trim()) &&
    !hasRows &&
    !(s.chips ?? []).length &&
    !(s.note ?? '').trim() &&
    !(s.imageUrl ?? '').trim()
  );
}

/** First non-empty string, or undefined. Keeps `??`/`||` from being mixed (a JS syntax error). */
function firstText(...vals: (string | undefined)[]): string | undefined {
  for (const v of vals) {
    const t = (v ?? '').trim();
    if (t) return t;
  }
  return undefined;
}

const paras = (text?: string): string[] =>
  (text ?? '')
    .split(/\n{2,}|\r\n\r\n/)
    .map((s) => s.trim())
    .filter(Boolean);

const lines = (text?: string): string[] =>
  (text ?? '')
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean);

/** Key/value pairs, or a two-column table, read as spec rows. Blank values are dropped. */
function toSpecRows(s: DetailSection): SpecRow[] {
  const out: SpecRow[] = [];
  for (const p of s.pairs ?? []) {
    const parameter = String(p?.label ?? '').replace(/\s+/g, ' ').trim();
    const value = String(p?.value ?? '').replace(/\s+/g, ' ').trim();
    if (parameter && value) out.push({ parameter, value });
  }
  if (!out.length && (s.columns ?? []).length === 2) {
    for (const row of s.rows ?? []) {
      // A stray tab + repeated number is an authoring slip in the live data ("Total flavonoids
      // (% by weight, min.)\t5" with value "5"). Keep the label, drop the duplicated tail.
      const parameter = String(row?.[0] ?? '').split('\t')[0].replace(/\s+/g, ' ').trim();
      const value = String(row?.[1] ?? '').replace(/\s+/g, ' ').trim();
      if (parameter && value) out.push({ parameter, value });
    }
  }
  return out;
}

/** A dose table, read positionally from whatever column order the section declares. */
function toDosageTable(s: DetailSection): ProductFacts['dosageTable'] | undefined {
  const columns = (s.columns ?? []).map((c) => String(c ?? '').trim());
  const rows = (s.rows ?? []).filter((r) => (r ?? []).some((c) => String(c ?? '').trim()));
  if (columns.length < 2 || !rows.length) return undefined;

  const idx = (re: RegExp) => columns.findIndex((c) => re.test(c));
  const cropAt = idx(/crop|cultivation|plant|application rate|turf/i);
  const appAt = idx(/^application$|spray|number|nos?\b/i);
  const whenAt = idx(/when|timing|time|stage|interval/i);
  const doseAt = idx(/dose|dosage|rate|quantity|qty/i);
  const noteAt = idx(/note|water|remark|volume/i);

  const cell = (r: string[], i: number) => (i >= 0 ? String(r[i] ?? '').replace(/\s+/g, ' ').trim() : '');

  const mapped: DosageRow[] = rows.map((r) => ({
    crop: cell(r, cropAt >= 0 ? cropAt : 0),
    application: cell(r, appAt) || undefined,
    timing: cell(r, whenAt) || undefined,
    dose: cell(r, doseAt >= 0 ? doseAt : columns.length - 1),
    note: cell(r, noteAt) || undefined,
  }));

  return { columns, rows: mapped, caption: (s.caption ?? '').trim() || undefined };
}

/**
 * Read every section once and name what it holds. Order of checks matters: `licence` is tested
 * before `packaging` for a standalone "Product Licence" section, but the seeded "Packaging, storage
 * & licence" section carries the licence in its `note`, so both paths are covered.
 */
export function extractProductFacts(product: Product): ProductFacts {
  const facts: ProductFacts = {
    description: product.fullDescription ?? [],
    compositionRows: [],
    specifications: [],
    dosageNotes: [],
    applicationMethods: [],
    usage: [],
    precautions: [],
    other: [],
  };

  for (const s of product.extraSections ?? []) {
    if (!s || isEmptySection(s)) continue; // an empty "Table" section is an artifact, not content
    const id = (s.id ?? '').toLowerCase();
    const title = (s.title ?? '').trim();

    if (id === 'overview' || RE.overview.test(title)) {
      if (!facts.description.length) facts.description = paras(s.text);
      else facts.other.push(s);
      continue;
    }

    if (id === 'composition' || RE.composition.test(title)) {
      facts.compositionSummary = firstText(facts.compositionSummary, s.text);
      facts.compositionRows.push(...toSpecRows(s));
      continue;
    }

    if (id === 'specifications' || RE.spec.test(title)) {
      facts.specifications.push(...toSpecRows(s));
      continue;
    }

    if (id === 'dosage' || RE.dosage.test(title)) {
      facts.dosageSummary = firstText(facts.dosageSummary, s.text);
      facts.dosageTable = facts.dosageTable ?? toDosageTable(s);
      facts.dosageNotes.push(...toSpecRows(s).filter(() => !(s.columns ?? []).length));
      if (s.chips?.length) facts.applicationMethods.push(...s.chips.map((c) => String(c).trim()).filter(Boolean));
      if (s.items?.length) facts.usage.push(...s.items.map((i) => String(i).trim()).filter(Boolean));
      continue;
    }

    if (id === 'usage' || RE.usage.test(title)) {
      facts.usage.push(...(s.items ?? []).map((i) => String(i).trim()).filter(Boolean));
      if (!(s.items ?? []).length) facts.usage.push(...lines(s.text));
      if (s.chips?.length) facts.applicationMethods.push(...s.chips.map((c) => String(c).trim()).filter(Boolean));
      continue;
    }

    if (id === 'precautions' || RE.precaution.test(title)) {
      facts.precautions.push(...(s.items ?? []).map((i) => String(i).trim()).filter(Boolean));
      if (!(s.items ?? []).length) facts.precautions.push(...lines(s.text));
      continue;
    }

    if (RE.warranty.test(title)) {
      facts.warranty = firstText(facts.warranty, paras(s.text).join(' '));
      continue;
    }

    // A section that is ONLY about the licence (no packaging content) — C4L's "Product Licence".
    if (RE.licence.test(title) && !RE.packaging.test(title)) {
      facts.licence = firstText(facts.licence, paras(s.text).join(' '));
      continue;
    }

    if (id === 'packaging' || RE.packaging.test(title)) {
      const pairs = toSpecRows(s);
      const shelf = pairs.find((p) => /shelf/i.test(p.parameter));
      const pack = pairs.find((p) => /packag|transport|storage/i.test(p.parameter));
      const body = paras(s.text);
      // "Shelf life: 3 years" is authored as a paragraph in the seeded shape and as a pair in the
      // hand-authored one. Read whichever is present; never assume a default.
      const shelfLine = body.find((p) => /^shelf life\s*:/i.test(p));
      facts.shelfLife = firstText(facts.shelfLife, shelf?.value, shelfLine?.replace(/^shelf life\s*:\s*/i, ''));
      facts.packagingInfo = firstText(facts.packagingInfo, pack?.value, body.filter((p) => p !== shelfLine).join(' '));
      facts.licence = firstText(facts.licence, s.note);
      continue;
    }

    // "Benefits" is already rendered as the Key-benefits list from `highlights`; a duplicate
    // Benefits section would print the same facts twice on one screen.
    if (RE.benefits.test(title) && (product.benefits ?? []).length) continue;

    facts.other.push(s);
  }

  // A licence can also arrive as the `note` of any section (the seeded shape uses the packaging one).
  if (!facts.licence) {
    for (const s of product.extraSections ?? []) {
      const note = (s?.note ?? '').trim();
      if (note && RE.licence.test(note)) {
        facts.licence = note;
        break;
      }
    }
  }

  facts.applicationMethods = [...new Set(facts.applicationMethods)];
  if (!facts.applicationMethods.length) facts.applicationMethods = [...(product.applicationTags ?? [])];

  // Shelf life is a named fact of its own (the glance and the packaging section both state it), so a
  // specifications ROW repeating it would be the same number printed twice.
  const shelfRow = facts.specifications.find((r) => /^shelf.?life$/i.test(r.parameter.replace(/\s+/g, ' ').trim()));
  facts.shelfLife = firstText(facts.shelfLife, shelfRow?.value);

  const seen = new Set(facts.compositionRows.map((r) => normParam(r.parameter)));
  facts.specifications = facts.specifications.filter((r) => {
    const key = normParam(r.parameter);
    if (/^shelf.?life$/.test(key)) return false;
    if (seen.has(key)) return false; // already stated, in more detail, under Composition
    seen.add(key);
    return true;
  });

  return facts;
}

/** Compare parameter names ignoring case, spacing and punctuation. */
function normParam(s: string): string {
  return String(s).toLowerCase().replace(/[^a-z0-9]+/g, '');
}

// ---------------------------------------------------------------------------
// Crop <-> dose-row matching
// ---------------------------------------------------------------------------

/**
 * One dose-table cell can name several crops ("Tomato / Capsicum / Chilli / Brinjal",
 * "Citrus (Orange / Lemon)", "Leafy vegetables & herbs"). Split it into the crop names it actually
 * lists, keeping any parenthesised names as names in their own right.
 */
export function splitCropCell(cell: string): string[] {
  return String(cell)
    .replace(/[()]/g, ' / ')
    .split(/[/,]|&|\band\b/gi)
    .map((s) => s.replace(/\s+/g, ' ').trim())
    .filter(Boolean);
}

/** Lower-case, de-plural, strip punctuation — so "Grapes" and "Grape" compare equal. */
function normCrop(name: string): string {
  const n = String(name).toLowerCase().replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();
  return n.endsWith('es') && n.length > 4 ? n.slice(0, -2) : n.endsWith('s') && n.length > 3 ? n.slice(0, -1) : n;
}

/**
 * Does this dose row apply to this crop? Matching is by WHOLE normalised name, never substring:
 * "corn" must not match "cornflower", and "onion" must not match "spring onion" unless the sheet
 * says so. `aliases` carries the crop-page's own documented synonyms (paddy/rice, citrus/orange…).
 */
export function doseRowMatchesCrop(row: DosageRow, cropNames: string[]): boolean {
  const wanted = new Set(cropNames.map(normCrop).filter(Boolean));
  return splitCropCell(row.crop).some((part) => wanted.has(normCrop(part)));
}

/** Every dose row in this product that is documented for the given crop (verbatim, never edited). */
export function doseRowsForCrop(facts: ProductFacts, cropNames: string[]): DosageRow[] {
  return (facts.dosageTable?.rows ?? []).filter((r) => doseRowMatchesCrop(r, cropNames));
}
